import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import validator from 'validator';
import messageService from '../components/Services/messageService';

const AuthContext = createContext();

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [session, setSession] = useState(null);

    useEffect(() => {
        // Nettoyer une seule fois les anciennes données
        const cleaned = localStorage.getItem('finai_cleaned_v2');
        if (!cleaned) {
            Object.keys(localStorage).forEach(key => {
                if (key.includes('finai_')) {
                    localStorage.removeItem(key);
                }
            });
            localStorage.setItem('finai_cleaned_v2', 'true');
        }

        // Récupérer la session active au démarrage
        const getSession = async () => {
            try {
                const { data: { session }, error } = await supabase.auth.getSession();

                if (error) {
                    console.error('Erreur lors de la récupération de la session:', error);
                } else {
                    setSession(session);
                    setUser(session?.user || null);

                    // // Si session active, configurer le token pour l'API FastAPI
                    // if (session?.user) {
                    //     await syncWithFastAPI(session.user);
                    // }
                }
            } catch (err) {
                console.error('Erreur session:', err);
            } finally {
                setIsLoading(false);
            }
        };

        getSession();

        // Écouter les changements d'authentification
        const { data: { subscription } } = supabase.auth.onAuthStateChange(
            async (event, session) => {
                console.log('Auth state changed:', event, session);

                setSession(session);
                setUser(session?.user || null);
                setIsLoading(false);

                if (event === 'SIGNED_IN') {
                    console.log('✅ User signed in:', session.user.email);
                    // await syncWithFastAPI(session.user);
                }

                if (event === 'SIGNED_OUT') {
                    console.log('👋 User signed out');
                    // Nettoyer les données FastAPI
                    messageService.logout();
                }
            }
        );

        return () => subscription.unsubscribe();
    }, []);

    // Validation d'email côté client
    const validateEmail = (email) => {
        if (!email) {
            throw new Error('L\'adresse email est requise');
        }

        if (!validator.isEmail(email)) {
            throw new Error('Format d\'email invalide');
        }

        // Vérifications supplémentaires
        if (email.length > 320) { // RFC 5321 limit
            throw new Error('Adresse email trop longue');
        }

        // Bloquer les emails temporaires (optionnel)
        const tempDomains = ['tempmail.org', '10minutemail.com', 'guerrillamail.com'];
        const domain = email.split('@')[1]?.toLowerCase();
        if (tempDomains.includes(domain)) {
            throw new Error('Les adresses email temporaires ne sont pas autorisées');
        }

        return true;
    };

    // Connexion par email avec OTP (One-Time Password)
    const signInWithEmail = async (email) => {
        try {
            // Validation côté client
            validateEmail(email);

            setIsLoading(true);

            const { data, error } = await supabase.auth.signInWithOtp({
                email: email.toLowerCase().trim(),
                options: {
                    // Configuration du code OTP
                    shouldCreateUser: true, // Créer l'utilisateur s'il n'existe pas
                    emailRedirectTo: `${window.location.origin}/dashboard`,
                }
            });

            if (error) {
                // Gestion des erreurs Supabase spécifiques
                if (error.message.includes('Invalid email')) {
                    throw new Error('Format d\'email invalide');
                }
                if (error.message.includes('Email rate limit')) {
                    throw new Error('Trop de tentatives. Réessayez dans quelques minutes');
                }
                throw new Error(error.message || 'Erreur lors de l\'envoi du code');
            }

            console.log('✅ Code OTP envoyé avec succès à:', email);
            return {
                success: true,
                message: 'Code de vérification envoyé par email',
                data
            };

        } catch (err) {
            console.error('Erreur signInWithEmail:', err);
            throw err;
        } finally {
            setIsLoading(false);
        }
    };

    // Vérification du code OTP
    const verifyOTP = async (email, token) => {
        try {
            setIsLoading(true);

            // Validation du code
            if (!token || token.length !== 6 || !/^\d{6}$/.test(token)) {
                throw new Error('Le code doit contenir exactement 6 chiffres');
            }

            const { data, error } = await supabase.auth.verifyOtp({
                email: email.toLowerCase().trim(),
                token: token,
                type: 'email' // ou 'magiclink' selon votre configuration
            });

            if (error) {
                // Gestion des erreurs spécifiques
                if (error.message.includes('Invalid token')) {
                    throw new Error('Code de vérification incorrect');
                }
                if (error.message.includes('Token expired')) {
                    throw new Error('Le code a expiré. Demandez un nouveau code');
                }
                throw new Error(error.message || 'Erreur lors de la vérification');
            }

            if (data.user) {
                console.log('✅ Utilisateur authentifié:', data.user.email);
                setUser(data.user);
                setSession(data.session);

                // La synchronisation FastAPI se fera automatiquement via onAuthStateChange

                return {
                    success: true,
                    user: data.user,
                    session: data.session
                };
            }

            throw new Error('Erreur inattendue lors de la vérification');

        } catch (err) {
            console.error('Erreur verifyOTP:', err);
            throw err;
        } finally {
            setIsLoading(false);
        }
    };

    // Connexion Google (optionnel, à configurer dans Supabase Dashboard)
    const signInWithGoogle = async () => {
        try {
            setIsLoading(true);

            const { data, error } = await supabase.auth.signInWithOAuth({
                provider: 'google',
                options: {
                    redirectTo: `${window.location.origin}/dashboard`,
                }
            });

            if (error) {
                throw new Error(error.message || 'Erreur lors de la connexion Google');
            }

            return { success: true, data };

        } catch (err) {
            console.error('Erreur signInWithGoogle:', err);
            throw err;
        } finally {
            setIsLoading(false);
        }
    };

    // Renvoyer un code OTP
    const resendOTP = async (email) => {
        try {
            return await signInWithEmail(email); // Même logique que l'envoi initial
        } catch (err) {
            throw new Error('Erreur lors du renvoi du code: ' + err.message);
        }
    };

    // Déconnexion
    const signOut = async () => {
        try {
            const { error } = await supabase.auth.signOut();

            if (error) {
                throw new Error(error.message);
            }

            setUser(null);
            setSession(null);

            // Nettoyer FastAPI aussi
            messageService.logout();

            console.log('👋 Déconnexion réussie');

        } catch (err) {
            console.error('Erreur lors de la déconnexion:', err);
            throw err;
        }
    };

    // Obtenir les informations du profil utilisateur
    const getUserProfile = async () => {
        if (!user) return null;

        try {
            const { data, error } = await supabase
                .from('profiles') // Table profiles (à créer si besoin)
                .select('*')
                .eq('id', user.id)
                .single();

            if (error && error.code !== 'PGRST116') {
                console.warn('Erreur profile:', error);
            }

            return data;
        } catch (err) {
            console.error('Erreur getUserProfile:', err);
            return null;
        }
    };

    // Vérifier si l'utilisateur est authentifié
    const isAuthenticated = () => {
        return !!(user && session);
    };

    // État de chargement global
    const loading = isLoading;

    const value = {
        // États
        user,
        session,
        isLoading: loading,

        // Méthodes d'authentification (EXACTEMENT comme avant)
        signInWithEmail,
        verifyOTP,
        signInWithGoogle,
        resendOTP,
        signOut,

        // Utilitaires
        isAuthenticated,
        getUserProfile,
        validateEmail,

        // Compatibilité avec l'ancien code
        login: verifyOTP,
        loginWithGoogle: signInWithGoogle,
        logout: signOut,
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};